import { ChangeEvent, useRef, useState } from "react";
import { AlertTriangle, FileText, Trash2, Upload } from "lucide-react";

import { useRemoveCert, useUploadCert } from "@/features/workers/model/useWorkerProfile";
import type { Certification } from "@/features/workers/types";
import { CERT_CATEGORIES, type CertCategory } from "@/shared/lib/certCategories";
import { cn } from "@/shared/lib/cn";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import {
  ALLOWED_DOC_TYPES,
  DOC_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

interface Props {
  certifications: Certification[];
}

function parseIsoDate(iso: string): Date {
  // Avoid TZ shifting — parse as local midnight.
  const [y, m, d] = iso.split("-").map((p) => parseInt(p, 10));
  return new Date(y, m - 1, d);
}

function expiryStatus(expires_on: string | null): {
  label: string;
  tone: "ok" | "warn" | "expired" | "none";
} {
  if (!expires_on) return { label: "No expiry set", tone: "none" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = parseIsoDate(expires_on);
  const daysLeft = Math.round((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: "Expired", tone: "expired" };
  if (daysLeft <= 30)
    return { label: `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`, tone: "warn" };
  return { label: `Valid until ${exp.toLocaleDateString()}`, tone: "ok" };
}

export function CertUpload({ certifications }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadCert();
  const remove = useRemoveCert();
  const [showForm, setShowForm] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [category, setCategory] = useState<CertCategory | "">("");
  const [expiresOn, setExpiresOn] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_DOC_TYPES);
    if (err) {
      setClientError(err);
      return;
    }
    setClientError(null);
    setPendingFile(file);
    setCategory("");
    setExpiresOn("");
    setSubmitError(null);
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!pendingFile) return;
    if (!category) {
      setSubmitError("Pick a certification category before saving.");
      return;
    }
    setSubmitError(null);
    upload.mutate(
      {
        file: pendingFile,
        category,
        expires_on: expiresOn || undefined,
      },
      {
        onSuccess: () => {
          setPendingFile(null);
          setCategory("");
          setExpiresOn("");
          setShowForm(false);
        },
      },
    );
  };

  const handleCancel = () => {
    setPendingFile(null);
    setCategory("");
    setExpiresOn("");
    setSubmitError(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {certifications.length === 0 && !showForm && (
          <p className="rounded-md border border-dashed border-input bg-card p-4 text-xs text-muted-foreground">
            No certifications uploaded yet. Smart Serve, Pro Serve, food handler — anything that
            speeds up your hire.
          </p>
        )}
        {certifications.map((cert) => {
          const status = expiryStatus(cert.expires_on);
          const label = cert.category ?? cert.name;
          return (
            <div
              key={cert.id}
              className="flex items-center gap-3 rounded-md border bg-card p-3"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <a
                  href={cert.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-sm font-medium hover:underline"
                  title={label}
                >
                  {label}
                </a>
                {!cert.category && (
                  <p className="text-xs text-warning-foreground">
                    No category set — re-upload to categorize.
                  </p>
                )}
                <div
                  className={cn(
                    "flex items-center gap-1 text-xs",
                    status.tone === "expired" && "text-destructive",
                    status.tone === "warn" && "text-warning-foreground",
                    (status.tone === "ok" || status.tone === "none") &&
                      "text-muted-foreground",
                  )}
                >
                  {(status.tone === "expired" || status.tone === "warn") && (
                    <AlertTriangle className="h-3 w-3" />
                  )}
                  {status.label}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(cert.id)}
                disabled={remove.isPending}
                className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-destructive disabled:opacity-50"
                aria-label={`Remove ${cert.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {showForm && pendingFile && (
        <div className="space-y-2 rounded-md border border-primary bg-card p-3">
          <p className="text-xs text-muted-foreground">
            Uploading <span className="font-medium">{pendingFile.name}</span>
          </p>
          <div className="space-y-1">
            <label htmlFor="cert-category" className="text-xs font-medium">
              Category
            </label>
            <Select
              value={category}
              onValueChange={(v) => {
                setCategory(v as CertCategory);
                setSubmitError(null);
              }}
            >
              <SelectTrigger id="cert-category">
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                {CERT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Same list venues pick from when requiring certs on a shift.
            </p>
          </div>
          <div className="space-y-1">
            <label htmlFor="cert-expires" className="text-xs font-medium">
              Expiration date <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="cert-expires"
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              We'll warn you 30 days before expiry.
            </p>
          </div>
          {submitError && (
            <p className="text-xs text-destructive-foreground">{submitError}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleCancel}
              disabled={upload.isPending}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={upload.isPending || !category}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {upload.isPending ? "Uploading…" : "Save certification"}
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            Add certification
          </button>
          <p className="text-xs text-copy-muted">
            PDF or image. Max 5MB. Upload one file at a time.
          </p>
          {clientError && (
            <p className="text-xs text-destructive-foreground">{clientError}</p>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={DOC_ACCEPT_ATTR}
            className="hidden"
            onChange={handleFilePicked}
          />
        </>
      )}
    </div>
  );
}
